/**
 * Game events for the playtest (docs/architecture.md §14, docs/playtest.md): sessions, fights per
 * session, next-day return, matchmaking wait, fight length and method, level-ups and skill
 * spending. They are read off the app state, so screens stay free of tracking calls.
 */
import { COSMETIC_KINDS, FIGHT, levelOf, lookOf, ownedItems, spentPoints, type SkillRanks } from '@caged/shared';
import type { MatchSetup } from '../game/session';
import { getDeviceMode } from '../input/device';
import { useApp, type AppState, type FightSummary } from '../state/store';
import { setContext, track } from './analytics';
import { isStandalone, isTouchPhone, playsUpright } from './gameMode';

declare const __BUILD_ID__: string;

const secondsSince = (ms: number) => Math.round((Date.now() - ms) / 100) / 10;

function device(): string {
  if (isTouchPhone()) return 'phone';
  return getDeviceMode().touch ? 'tablet' : 'desktop';
}

function opponentOf(m: MatchSetup) {
  const league = m.online?.info.league;
  return {
    mode: m.online ? 'online' : 'offline',
    opponent: league?.plan ? 'plan' : m.opponent.bot || m.online?.info.vsBot ? 'bot' : 'human',
    ...(m.opponent.bot ? { difficulty: m.opponent.bot.difficulty } : {}),
    // A league fight (M5), against a player, a bot's place, or an absent player's game plan.
    league: !!league,
  };
}

/** Seconds of fighting: a KO or forfeit ends mid-round, a decision goes the distance. */
function fightSeconds(f: FightSummary): number {
  const r = f.result;
  if (r.method === 'decision' || r.method === 'draw') return FIGHT.rounds * FIGHT.roundSeconds;
  return Math.round((r.round - 1) * FIGHT.roundSeconds + r.roundTime);
}

function fightEnd(f: FightSummary) {
  const { result, outcome, rewards, net } = f;
  return {
    ...opponentOf(f.match),
    outcome,
    method: result.method,
    round: result.round,
    seconds: fightSeconds(f),
    connection_lost: !!f.connectionLost,
    // Practice (offline, D26) earns nothing; null rewards online: the server's didn't arrive.
    practice: !!f.practice,
    xp: rewards?.xp.total ?? 0,
    ...(rewards ? { level: rewards.levelAfter.level } : {}),
    ...(net
      ? {
          ping_avg: net.pingAvg,
          ping_max: net.pingMax,
          reconnects: net.reconnects,
          opponent_away: net.opponentAway,
          resyncs: net.resyncs,
        }
      : {}),
  };
}

/** Ranks gained per skill (a Training confirm). */
function gained(before: SkillRanks, after: SkillRanks): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, rank] of Object.entries(after) as [keyof SkillRanks, number][]) {
    if (rank > before[id]) out[id] = rank - before[id];
  }
  return out;
}

function onChange(s: AppState, prev: AppState): void {
  if (s.settings.language !== prev.settings.language) {
    setContext({ language: s.settings.language });
    track('language_change', { from: prev.settings.language, to: s.settings.language });
  }
  if (s.screen !== prev.screen) track('screen_view', { screen: s.screen, from: prev.screen });

  // Matchmaking: a new search, a match, the offline fallback, or backing out.
  const mm = s.matchmaking;
  const was = prev.matchmaking;
  if (mm && mm.since !== was?.since) track('matchmaking_start', { online: mm.status === 'searching', league: !!mm.league });
  if (mm && was && mm.since === was.since && mm.status !== was.status) {
    if (mm.status === 'found') track('match_found', { opponent: mm.vsBot ? 'bot' : 'human', wait_s: secondsSince(mm.since) });
    if (mm.status === 'offline') track('matchmaking_offline', { wait_s: secondsSince(mm.since) });
  }
  if (!mm && was && s.screen === 'home') track('matchmaking_cancel', { status: was.status, wait_s: secondsSince(was.since) });

  if (s.screen === 'fight' && prev.screen !== 'fight' && s.match) {
    // A league fight's replay is watched, not fought.
    if (s.match.watch) track('replay_watch', { day: s.match.watch.day });
    else track('fight_start', { ...opponentOf(s.match), rejoined: prev.screen === 'home', upright: playsUpright() });
  }
  if (s.matchmaking?.league?.failed && !prev.matchmaking?.league?.failed) track('league_lobby_failed', { reason: s.matchmaking.league.failed });
  if (s.lastFight && s.lastFight !== prev.lastFight) {
    track('fight_end', fightEnd(s.lastFight));
    const r = s.lastFight.rewards;
    if (r && r.levelAfter.level > r.levelBefore.level) track('level_up', { level: r.levelAfter.level });
  }
  if (prev.rejoin && !s.rejoin) track('rejoin', { result: s.screen === 'fight' ? 'rejoined' : 'left' });

  const before = prev.progress.ranks;
  const after = s.progress.ranks;
  if (after !== before) {
    const spent = spentPoints(after) - spentPoints(before);
    if (spent > 0) track('skills_confirmed', { points: spent, ranks: gained(before, after), total_points: spentPoints(after) });
    else if (spent < 0) track('skills_reset', { points_back: -spent });
  }

  // The Locker (M7): what sells, and what people wear.
  if (s.progress !== prev.progress) {
    const had = ownedItems(prev.progress);
    for (const item of ownedItems(s.progress)) {
      if (!had.includes(item)) track('locker_buy', { item, coins_left: s.progress.coins });
    }
    const was = lookOf(prev.progress);
    const now = lookOf(s.progress);
    if (COSMETIC_KINDS.some((k) => was[k] !== now[k])) track('locker_look', { ...now });
  }
}

export function startTracking(): () => void {
  const s = useApp.getState();
  const { wins, losses, draws } = s.progress.record;
  setContext({
    // The commit: before and after a tuning change can be compared.
    build: __BUILD_ID__,
    language: s.settings.language,
    device: device(),
    installed: isStandalone(),
  });
  track('app_open', {
    level: levelOf(s.progress).level,
    fights: wins + losses + draws,
    rejoin_offered: !!s.rejoin,
  });
  return useApp.subscribe(onChange);
}
